import { PatientSummarizationStatusCard } from '@ctw/shared/content/summarization/patient-summarization-status-card';
import { PatientSummaryJobProvider } from '@ctw/shared/content/summarization/patient-summary-job-provider';
import { useUserPatientSummarizationContext } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Encapsulates the summarization status card for the patient overview page.
 * Checks for a cached summary on mount and reads job state from the global
 * summarization context. Polling and toast notifications persist across
 * tab navigation within the patient since job state lives in the context.
 */
export function useOverviewSummarizationCard(): ReactNode {
  const hasSummarization = useFeatureFlag('ctw-hpi-standalone-zap');
  const hasSummarizationStreaming = useFeatureFlag('ctw-summarization-v2-ui-refresh');
  const { activeSummaryJob, summaryResponse, generateSummary, checkCachedSummary } =
    useUserPatientSummarizationContext();
  const { upsell } = useBuilderConfig();
  const { patient } = usePatientContext();

  const patientUpid = patient.UPID;
  const isCurrentPatientJob =
    activeSummaryJob !== null &&
    patientUpid !== undefined &&
    activeSummaryJob.patientUpid === patientUpid;
  // Only surface the shared summary state to this card when it actually belongs to the
  // patient currently in view — otherwise stale state from a prior patient could leak in
  const cardResult = isCurrentPatientJob ? summaryResponse : null;

  const [isCheckingCache, setIsCheckingCache] = useState(false);

  const handleGenerate = useCallback(() => {
    if (patientUpid) {
      generateSummary(patientUpid);
    }
  }, [generateSummary, patientUpid]);

  // Check for a cached summary when opening a patient page.
  const cacheCheckedForUpidRef = useRef<string | null>(null);
  useEffect(() => {
    if (patientUpid && cacheCheckedForUpidRef.current !== patientUpid) {
      cacheCheckedForUpidRef.current = patientUpid;
      setIsCheckingCache(true);
      void checkCachedSummary(patientUpid).finally(() => {
        setIsCheckingCache(false);
      });
    }
  }, [patientUpid, checkCachedSummary]);

  if (!(hasSummarization && hasSummarizationStreaming)) {
    return null;
  }

  return (
    <PatientSummaryJobProvider summaryResponse={summaryResponse}>
      <PatientSummarizationStatusCard
        patientSummarizationResult={cardResult}
        onGenerateSummary={handleGenerate}
        isCheckingCache={isCheckingCache}
        upsell={upsell}
      />
    </PatientSummaryJobProvider>
  );
}
